/** Angular Imports */
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

/** Custom Services */
import { AccountingService } from '../../accounting.service';
import { GlAccountTreeSelectorComponent } from '../../../shared/accounting/gl-account-tree-selector/gl-account-tree-selector.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { STANDALONE_SHARED_IMPORTS } from 'app/standalone-shared.module';
import { GLAccount } from 'app/shared/models/general.model';

/**
 * Edit gl account component.
 */
@Component({
  selector: 'LOSx-edit-gl-account',
  templateUrl: './edit-gl-account.component.html',
  styleUrls: ['./edit-gl-account.component.scss'],
  imports: [
    ...STANDALONE_SHARED_IMPORTS,
    GlAccountTreeSelectorComponent,
    MatCheckbox,
    CdkTextareaAutosize
  ]
})
export class EditGlAccountComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private accountingService = inject(AccountingService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** GL account form. */
  glAccountForm: UntypedFormGroup;
  /** GL account and chart of accounts data. */
  glAccount: any;
  /** Account type data. */
  accountTypeData: any;
  /** Account usage data. */
  accountUsageData: any;
  /** Full chart of accounts used by the parent tree selector. */
  allGlAccounts: GLAccount[] = [];
  /** IDs that cannot be selected as parent (current account and its descendants). */
  excludedParentIds: number[] = [];
  /** Tag data. */
  tagData: any;

  /**
   * Retrieves the chart of accounts data from `resolve`.
   * @param {FormBuilder} formBuilder Form Builder.
   * @param {AccountingService} accountingService Accounting Service.
   * @param {ActivatedRoute} route Activated Route.
   * @param {Router} router Router for navigation.
   */
  constructor() {
    this.route.data.subscribe((data: { glAccountAndChartOfAccountsTemplate: any }) => {
      this.glAccount = data.glAccountAndChartOfAccountsTemplate;
    });
  }

  /**
   * Creates and sets gl account form.
   */
  ngOnInit() {
    this.createGlAccountForm();
    this.setGLAccountForm();
    this.accountingService.getChartOfAccounts().subscribe((glAccounts: GLAccount[]) => {
      this.allGlAccounts = glAccounts;
      this.excludedParentIds = this.collectDescendantIds(glAccounts, this.glAccount.id);
    });
  }

  /**
   * Creates gl account form.
   */
  createGlAccountForm() {
    this.glAccountForm = this.formBuilder.group({
      type: [
        '',
        Validators.required
      ],
      name: [
        this.glAccount.name,
        Validators.required
      ],
      usage: [
        this.glAccount.usage.id,
        Validators.required
      ],
      glCode: [
        { value: this.glAccount.glCode, disabled: true }
      ],
      parentId: [this.glAccount.parentId],
      tagId: [this.glAccount.tagId.id],
      manualEntriesAllowed: [
        this.glAccount.manualEntriesAllowed,
        Validators.required
      ],
      description: [this.glAccount.description]
    });
  }

  /**
   * Sets gl account form for selected account type.
   */
  setGLAccountForm() {
    this.accountTypeData = this.glAccount.accountTypeOptions;
    this.accountUsageData = this.glAccount.usageOptions;
    this.glAccountForm.get('type').valueChanges.subscribe((accountTypeId) => {
      switch (accountTypeId) {
        case 1:
          this.tagData = this.glAccount.allowedAssetsTagOptions;
          break;
        case 2:
          this.tagData = this.glAccount.allowedLiabilitiesTagOptions;
          break;
        case 3:
          this.tagData = this.glAccount.allowedEquityTagOptions;
          break;
        case 4:
          this.tagData = this.glAccount.allowedIncomeTagOptions;
          break;
        case 5:
          this.tagData = this.glAccount.allowedExpensesTagOptions;
          break;
      }
    });

    this.glAccountForm.get('type').setValue(this.glAccount.type.id);
  }

  /**
   * Submits the gl account form and updates gl account,
   * if successful redirects to view updated account.
   */
  submit() {
    const formValue = this.glAccountForm.value;
    delete formValue.glCode;
    this.accountingService.updateGlAccount(this.glAccount.id, formValue).subscribe((response: any) => {
      this.router.navigate(
        [
          '../../',
          response.resourceId
        ],
        { relativeTo: this.route }
      );
    });
  }

  /**
   * Collects the IDs of an account and all of its descendants.
   * @param {GLAccount[]} glAccounts Full chart of accounts.
   * @param {number} rootId Account id to start from.
   * @returns {number[]} All descendant ids plus the root id.
   */
  private collectDescendantIds(glAccounts: GLAccount[], rootId: number): number[] {
    const result: number[] = [rootId];
    const children = glAccounts.filter((account) => account.parentId === rootId);
    for (const child of children) {
      result.push(...this.collectDescendantIds(glAccounts, child.id));
    }
    return result;
  }
}
